import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { RECAPTCHA_CONFIG } from '../../helpers/url_helper';
import { TextField, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReCAPTCHA from 'react-google-recaptcha';

const ContactUsForm = ({
  defaultCategory = 'enterprise',
  onSuccess,
  submitLabel = 'Submit',
  secondaryAction = null,
  secondaryFirst = false,
}) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    description: '',
    category: defaultCategory || 'enterprise',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  
  // reCAPTCHA site key from config
  const RECAPTCHA_SITE_KEY = RECAPTCHA_CONFIG?.SITE_KEY || '';
  
  // Check if reCAPTCHA script is loaded
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) {
      console.log('reCAPTCHA Site Key loaded:', RECAPTCHA_SITE_KEY);
      
      // Function to check if script is loaded
      const checkRecaptchaLoaded = () => {
        if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.render) {
          console.log('reCAPTCHA script loaded successfully');
          setIsRecaptchaReady(true);
          setCaptchaError(null);
          return true;
        }
        return false;
      };
      
      // Check immediately
      if (checkRecaptchaLoaded()) {
        return;
      }
      
      // Check periodically if not loaded yet
      const checkInterval = setInterval(() => {
        if (checkRecaptchaLoaded()) {
          clearInterval(checkInterval);
        }
      }, 500);
      
      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        if (!checkRecaptchaLoaded()) {
          console.error('reCAPTCHA script failed to load after 10 seconds');
          setCaptchaError('reCAPTCHA script failed to load. Please check your internet connection and refresh the page.');
          setIsRecaptchaReady(false);
        }
      }, 10000);
      
      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    } else {
      console.warn('reCAPTCHA Site Key not found in config');
    }
  }, [RECAPTCHA_SITE_KEY]);

  const customTheme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: '#26C3C0' },
      background: { default: mode === 'dark' ? '#2a2a2a' : '#f8f9fa', paper: mode === 'dark' ? '#2a2a2a' : '#f8f9fa' },
      text: { primary: mode === 'dark' ? '#FFFFFF' : '#222222', secondary: mode === 'dark' ? '#666' : '#999' },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '& fieldset': { borderColor: mode === 'dark' ? '#444' : '#ddd' },
              '&:hover fieldset': { borderColor: '#26C3C0' },
              '&.Mui-focused fieldset': { borderColor: '#26C3C0' },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#666' : '#999',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '&.Mui-focused': { color: '#26C3C0' },
            },
          },
        },
      }
    }
  }), [mode]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const hasEmpty = (v) => !v || !String(v).trim();
  const isValid = () => ![
    formData.name,
    formData.company_name,
    formData.email,
    formData.description,
  ].some(hasEmpty);

  const handleCaptchaChange = (value) => {
    console.log('reCAPTCHA value changed:', value ? 'Verified' : 'Empty');
    setCaptchaValue(value);
    setCaptchaError(null);
    if (error && error.includes('CAPTCHA')) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowValidation(true);
    
    // Validate form fields
    if (!isValid()) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Validate CAPTCHA if site key is configured
    if (RECAPTCHA_SITE_KEY && !captchaValue) {
      setError('Please complete the CAPTCHA verification');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const response = await dataAPI.postEnquiry({ 
        name: formData.name,
        company_name: formData.company_name,
        email: formData.email,
        description: formData.description,
        category: formData.category,
        // Include CAPTCHA token if available (backend should verify it)
        ...(captchaValue && { recaptcha_token: captchaValue })
      });
      
      // Check if response has status "error" even if HTTP status is 200
      if (response && response.status === 'error') {
        setError(response.message || response.error || 'Failed to submit. Please try again.');
        setShowValidation(false);
        setIsSubmitted(false);
        return;
      }
      
      setFormData({ name: '', company_name: '', email: '', description: '', category: defaultCategory || 'enterprise' });
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setShowValidation(false);
      setIsSubmitted(true);
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to submit');
      setIsSubmitted(false);
      // Reset CAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Thank you section
  if (isSubmitted) {
    return (
      <div style={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: '80px', 
            color: '#26C3C0', 
            marginBottom: '24px' 
          }} 
        />
        <h2 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          fontFamily: "'Urbanist', Arial, sans-serif",
          margin: '0 0 16px 0',
          lineHeight: '1.2'
        }}>
          Thank you
        </h2>
        <p style={{
          fontSize: '16px',
          color: mode === 'dark' ? '#CCCCCC' : '#666666',
          fontFamily: "'Urbanist', Arial, sans-serif",
          margin: '0 0 32px 0',
          lineHeight: '1.6',
          maxWidth: '400px'
        }}>
          Our sales team will contact you as soon as possible. We appreciate your interest!
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 32px',
            backgroundColor: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: "'Urbanist', Arial, sans-serif",
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#009391';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#26C3C0';
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        {error && (
          <div style={{
            marginBottom: '16px',
            backgroundColor: '#1a0000',
            border: '1px solid #ff4444',
            borderRadius: '8px',
            padding: '12px 16px'
          }}>
            <span style={{ color: '#ffffff', fontSize: '14px', fontFamily: "'Urbanist', Arial, sans-serif" }}>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
          <TextField required size="small" label="Name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} error={showValidation && hasEmpty(formData.name)} fullWidth />
          <TextField required size="small" label="Company Name" value={formData.company_name} onChange={(e) => handleChange('company_name', e.target.value)} error={showValidation && hasEmpty(formData.company_name)} fullWidth />
          <TextField required type="email" size="small" label="Email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} error={showValidation && hasEmpty(formData.email)} fullWidth />
          <TextField required size="small" label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} error={showValidation && hasEmpty(formData.description)} fullWidth multiline minRows={4} />
          
          {/* reCAPTCHA */}
          {RECAPTCHA_SITE_KEY ? (
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
              {isRecaptchaReady ? (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={() => {
                    console.log('reCAPTCHA expired');
                    setCaptchaValue(null);
                    setCaptchaError('CAPTCHA expired. Please verify again.');
                    setError('CAPTCHA expired. Please verify again.');
                  }}
                  onError={(error) => {
                    console.error('reCAPTCHA error:', error);
                    setCaptchaValue(null);
                    setCaptchaError('CAPTCHA error. Please try again.');
                    setError('CAPTCHA error. Please try again.');
                  }}
                  onErrored={() => {
                    console.error('reCAPTCHA errored - script failed to load');
                    setCaptchaError('Failed to load CAPTCHA. Please refresh the page.');
                    setIsRecaptchaReady(false);
                  }}
                  theme={mode === 'dark' ? 'dark' : 'light'}
                  size="normal"
                />
              ) : (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: mode === 'dark' ? '#999' : '#666',
                  textAlign: 'center',
                  minWidth: '200px'
                }}>
                  {captchaError ? 'Failed to load reCAPTCHA' : 'Loading reCAPTCHA...'}
                </div>
              )}
              {captchaError && (
                <p style={{ 
                  color: '#ff4444', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  {captchaError}
                </p>
              )}
            </div>
          ) : (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px', 
              backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
              borderRadius: '4px',
              fontSize: '12px',
              color: mode === 'dark' ? '#999' : '#666',
              textAlign: 'center'
            }}>
              reCAPTCHA not configured
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          {secondaryFirst && secondaryAction && (
            <div style={{ display: 'flex' }}>
              {secondaryAction}
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !isValid()}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: isValid() && !isSubmitting 
                ? '#26C3C0' 
                : (mode === 'dark' 
                  ? '#444' 
                  : 'rgba(38, 195, 192, 0.5)'), // Theme green with 50% opacity for light theme disabled state
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: isValid() && !isSubmitting ? 'pointer' : 'not-allowed',
              fontFamily: "'Urbanist', Arial, sans-serif",
              opacity: isValid() && !isSubmitting ? 1 : (mode === 'dark' ? 0.6 : 1)
            }}
          >
            {isSubmitting ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
                Submitting...
              </span>
            ) : submitLabel}
          </button>
          {!secondaryFirst && secondaryAction && (
            <div style={{ display: 'flex' }}>
              {secondaryAction}
            </div>
          )}
        </div>
      </form>
    </ThemeProvider>
  );
};

export default ContactUsForm;


